import {
  FormControlProps,
  OutlinedInput,
  OutlinedInputProps,
  Stack,
  Tooltip,
  Typography,
  useForkRef,
} from "@mui/material";
import { propEq } from "ramda";
import { isNilOrEmpty, isNotNilOrEmpty } from "ramda-adjunct";
import React, { ForwardedRef, forwardRef, ReactElement, useCallback, useRef, useState } from "react";
import { FormControlWrapper, InputWrapperProps } from "@sinch/components";
import { ConditionalWrapper } from "@sinch/components/ConditionalWrapper/ConditionalWrapper";
import { LanguageTab, LanguageTabs } from "@sinch/components/form/MultilanguageTextInput/LanguageTabs";
import { useLanguageTabs } from "@sinch/components/form/MultilanguageTextInput/UseLanguageTabs";
import { useLanguageTextInput } from "@sinch/components/form/MultilanguageTextInput/UseLanguageTextInput";

type InputValue = { language: string; value: string | null }[];

export type MultilanguageTextInputProps = Omit<OutlinedInputProps, "type" | "value" | "onChange"> &
  Omit<InputWrapperProps, "slots" | "slotProps"> & {
    FormControlProps?: FormControlProps;
    value?: InputValue;
    onChange?: (value: InputValue) => void;
    languages: readonly { language: string; label: string; title?: string }[];
  };

export const MultilanguageTextInputBase = (
  {
    label,
    FormHelperTextProps: formHelperTextProps,
    FormControlProps: formControlProps,
    helperText,
    errorMessage,
    error,
    required,
    infoTooltip,
    value = [],
    onChange,
    languages,
    ...props
  }: MultilanguageTextInputProps,
  ref: ForwardedRef<HTMLDivElement | undefined> | undefined
): ReactElement => {
  if (languages.length === 0) {
    throw new Error("Cannot create language select text input without languages definition");
  }

  const [prevLanguages, setPrevLanguages] = useState(languages);
  if (languages !== prevLanguages) {
    setPrevLanguages(languages);
    onChange?.(
      languages.map(({ language }) => ({
        language: language,
        value: value.find(propEq("language", language))?.value ?? "",
      }))
    );
  }

  const inputRef = useRef<HTMLInputElement>(null);
  const forkRef = useForkRef(props.inputRef, inputRef);

  const initialLanguage = languages[0].language;
  const { selectedLang, onLangSelect, onMouseDown } = useLanguageTabs({
    initialLanguage: initialLanguage,
    inputRef,
  });

  const resetLangSelection = useCallback(() => onLangSelect(initialLanguage), [initialLanguage, onLangSelect]);

  // Get the first language value for use as placeholder in other languages
  const firstLanguageValue = value.find((item) => item.language === initialLanguage)?.value ?? "";

  const {
    focused,
    value: currentValue,
    onChange: handleChange,
    onFocus,
    onBlur,
  } = useLanguageTextInput({
    languages,
    selectedLang,
    value,
    onChange,
    resetLangSelection: resetLangSelection,
  });

  return (
    <FormControlWrapper
      error={error}
      errorMessage={errorMessage}
      focused={focused}
      FormHelperTextProps={formHelperTextProps}
      helperText={helperText}
      infoTooltip={infoTooltip}
      label={label}
      required={required}
      {...formControlProps}
    >
      <Stack>
        <OutlinedInput
          ref={ref}
          error={error}
          {...props}
          inputProps={{ ...props.inputProps, autoComplete: "off" }}
          inputRef={forkRef}
          onBlur={onBlur}
          onChange={handleChange}
          onFocus={onFocus}
          placeholder={selectedLang !== initialLanguage ? firstLanguageValue : props.placeholder}
          slotProps={{ input: props.inputProps }}
          value={currentValue}
        />
        {languages.length > 1 && (
          <LanguageTabs
            disabled={props.disabled}
            error={error}
            focused={focused}
            onChange={onLangSelect}
            onMouseDown={onMouseDown}
            slots={{ indicator: () => null }}
            value={selectedLang}
          >
            {languages.map(({ language, label, title }) => {
              const isFirstLanguage = language === initialLanguage;
              const isEmpty = required && isNilOrEmpty(value.find((item) => item.language === language)?.value);
              return (
                <LanguageTab
                  key={language}
                  disabled={props.disabled}
                  isEmpty={isEmpty}
                  label={
                    <ConditionalWrapper
                      condition={isNotNilOrEmpty(title)}
                      wrapper={(children) => <Tooltip title={title}>{children}</Tooltip>}
                    >
                      <span>
                        {label}
                        {required && isFirstLanguage && (
                          <Typography color="error" component="span">
                            *
                          </Typography>
                        )}
                      </span>
                    </ConditionalWrapper>
                  }
                  value={language}
                />
              );
            })}
          </LanguageTabs>
        )}
      </Stack>
    </FormControlWrapper>
  );
};

export const MultilanguageTextInput = forwardRef(MultilanguageTextInputBase) as (
  props: MultilanguageTextInputProps
) => ReturnType<typeof MultilanguageTextInputBase>;
